/**
 * Per-template upload generations.
 *
 * A custom-bg upload is async, and the slot it writes (`customBgs[id]`) stays
 * editable while it is in flight: the user can pick another file, hit Remove,
 * or unlock the template. A completion that lands after any of those must be
 * discarded — otherwise a late success resurrects an abandoned image and a late
 * failure clears the newer one.
 *
 * Each upload takes a token at `begin(id)`; anything that supersedes it bumps
 * the id's generation, so `isCurrent(id, token)` reads false forever after.
 * Tokens are per template id, so two templates uploading at once don't cancel
 * each other.
 */
export type UploadGenerations = {
  /** Start an upload for `id` and return its token. Supersedes any earlier one. */
  begin(id: string): number;
  /** Abandon whatever is in flight for `id` (Remove, unlock, unmount). */
  invalidate(id: string): void;
  /** True only while `token` is still the newest upload for `id`. */
  isCurrent(id: string, token: number): boolean;
};

export function createUploadGenerations(): UploadGenerations {
  const current = new Map<string, number>();
  const bump = (id: string): number => {
    const next = (current.get(id) ?? 0) + 1;
    current.set(id, next);
    return next;
  };
  return {
    begin: bump,
    invalidate: (id) => {
      bump(id);
    },
    isCurrent: (id, token) => current.get(id) === token,
  };
}
